import React, { memo } from 'react';
import { AppNode } from '@/types';
import { AlertCircle } from 'lucide-react';
import { GridOverlay } from '../GridOverlay';
import { VideoProgressBar } from './VideoProgressBar';
import { NODE_TYPE_ICON_URLS } from '../NodeTitle';

export const GeneratorContent: React.FC<{
  data: AppNode;
  isWorking: boolean;
  hasError: boolean;
  hasMedia: boolean;
  showImageGrid: boolean;
  gridSplit: { rows: number; cols: number } | null;
  activeFeatureGrid: { rows: number; cols: number } | null;
  selectedCells: Set<string>;
  onSelectedCellsChange: (cells: Set<string>) => void;
  onNodeUpdate: (id: string, data: Partial<AppNode['data']>, size?: { width?: number; height?: number }, title?: string) => void;
  mediaRef: React.RefObject<HTMLImageElement | HTMLVideoElement | HTMLAudioElement | null>;
  IconComponent: React.ComponentType<{ size?: number; className?: string }>;
  isHovered: boolean;
  selected: boolean;
  isPlayingVideo: boolean;
  videoProgress: number;
  videoDuration: number;
  videoVolume: number;
  isVideoMuted: boolean;
  hasAudioTrack: boolean;
  onToggleVideoPlay: () => void;
  onToggleVideoMute: () => void;
  onChangeVideoVolume: (volume: number) => void;
  onVideoTimeUpdate: () => void;
  onVideoSeek: (time: number) => void;
  onVideoEnded: () => void;
  onVideoLoadedMetadata: () => void;
  onExtractImage?: (nodeId: string, image: string) => void;
}> = memo(({
  data,
  isWorking,
  hasError,
  hasMedia,
  showImageGrid,
  gridSplit,
  activeFeatureGrid,
  selectedCells,
  onSelectedCellsChange,
  onNodeUpdate,
  mediaRef,
  IconComponent,
  isHovered,
  selected,
  isPlayingVideo,
  videoProgress,
  videoDuration,
  videoVolume,
  isVideoMuted,
  hasAudioTrack,
  onToggleVideoPlay,
  onToggleVideoMute,
  onChangeVideoVolume,
  onVideoTimeUpdate,
  onVideoSeek,
  onVideoEnded,
  onVideoLoadedMetadata,
  onExtractImage,
}) => {
  const showVideoControls = isHovered || selected;

  return (
    <>
      {!hasMedia ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {(() => {
            const iconUrl = NODE_TYPE_ICON_URLS[data.type];
            const opacityClass = isWorking ? 'opacity-90' : 'opacity-90';
            if (iconUrl) {
              return <img src={iconUrl} alt="" className={`transition-opacity duration-300 ${opacityClass}`} style={{ width: 48, height: 48 }} draggable={false} />;
            }
            return <IconComponent size={48} className={`transition-opacity duration-300 ${isWorking ? 'text-cyan-400/90' : 'text-white/90'}`} />;
          })()}
          {data.data.generationMode === 'UPSCALE' && (
            <span className="mt-3 text-xs text-white/50">配置参数生成高清图像</span>
          )}
        </div>
      ) : (
        <>
          {data.data.image && (
            <img
              ref={mediaRef as any}
              src={data.data.image}
              className="w-full h-full object-cover bg-[#262626]"
              draggable={false}
              style={{ filter: showImageGrid ? 'blur(10px)' : 'none' }}
            />
          )}
          {data.data.videoUri && (
            <div className="relative w-full h-full">
              <video
              key={data.data.videoUri}
              ref={mediaRef as any}
              data-video-node={data.id}
              src={data.data.videoUri}
              className="w-full h-full object-cover bg-[#262626]"
              loop
              playsInline
              preload="metadata"
              onTimeUpdate={onVideoTimeUpdate}
              onEnded={onVideoEnded}
              onLoadedMetadata={onVideoLoadedMetadata}
            />
              <VideoProgressBar
                nodeId={data.id}
                isPlaying={isPlayingVideo}
                progress={videoProgress}
                duration={videoDuration}
                volume={videoVolume}
                isMuted={isVideoMuted}
                hasAudioTrack={hasAudioTrack}
                visible={showVideoControls}
                onTogglePlay={onToggleVideoPlay}
                onToggleMute={onToggleVideoMute}
                onChangeVolume={onChangeVideoVolume}
                onSeek={onVideoSeek}
                onExtractImage={onExtractImage}
              />
            </div>
          )}
          {showImageGrid && data.data.images && data.data.images.length > 1 && (
            <div className="absolute inset-0 bg-black/40 z-10 grid grid-cols-2 gap-2 p-2 animate-in fade-in duration-200">
              {data.data.images.map((img, idx) => (
                <div
                  key={idx}
                  className={`relative rounded-lg overflow-hidden cursor-default border-2 bg-[#262626] ${img === data.data.image ? 'border-cyan-500' : 'border-transparent hover:border-white/50'}`}
                  onClick={(e) => { e.stopPropagation(); onNodeUpdate(data.id, { image: img }); }}
                >
                  <img src={img} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
          {gridSplit && data.data.image && (
            <GridOverlay
              rows={gridSplit.rows}
              cols={gridSplit.cols}
              interactive
              selectedCells={selectedCells}
              onCellClick={(cellId) => {
                const next = new Set(selectedCells);
                if (next.has(cellId)) { next.delete(cellId); } else { next.add(cellId); }
                onSelectedCellsChange(next);
              }}
            />
          )}
          {activeFeatureGrid && (activeFeatureGrid.rows > 1 || activeFeatureGrid.cols > 1) && !gridSplit && (
            <GridOverlay rows={activeFeatureGrid.rows} cols={activeFeatureGrid.cols} />
          )}
          {hasError && (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center z-20">
              <AlertCircle className="text-red-500 mb-2" size={20} />
              <span className="text-xs text-red-200">{data.data.error}</span>
            </div>
          )}
        </>
      )}
    </>
  );
});
